import React, { useState, useEffect, useCallback } from 'react';
import { List, Icon } from 'semantic-ui-react';

function ExpandableList({ items }) {
  const [expandedIndices, setExpandedIndices] = useState([]);

  const toggle = useCallback((index) => {
    setExpandedIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  }, []);

  // Auto-expand and smooth-scroll to the item matching the URL hash
  const expandFromHash = useCallback(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const idx = items.findIndex((item) => item.id === hash);
    if (idx === -1) return;

    // Clear hash immediately so it won't re-trigger on remounts
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setExpandedIndices((prev) =>
      prev.includes(idx) ? prev : [...prev, idx]
    );

    // Small delay to let React render the expanded content, then smooth-scroll
    setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [items]);

  useEffect(() => {
    expandFromHash();
    window.addEventListener('hashchange', expandFromHash);
    return () => window.removeEventListener('hashchange', expandFromHash);
  }, [expandFromHash]);

  // Prepend spinner to each child element from data
  const wrapWithSpinner = (child, childIndex) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, {}, [
      <span key="spin" className="agent-spinner" style={{ animationDelay: `${childIndex * 0.5}s` }} />,
      <span key="text" className="agent-item-text">{child.props.children}</span>
    ]);
  };

  return (
    <div>
      {items.map((item, index) => {
        const expanded = expandedIndices.includes(index);
        return (
          <div key={index} className="agent-sub-window" id={item.id}>
            {/* Titlebar with dropdown arrow on left */}
            <div
              className="agent-sub-window__titlebar"
              onClick={(e) => {
                if (e.target.closest("a")) return;
                toggle(index);
              }}
            >
              <Icon name={`angle ${expanded ? 'down' : 'right'}`} className="agent-sub-window__toggle" />
              <span className="agent-sub-window__title">{item.title}</span>
              <span className="agent-sub-window__dot" style={{ animationDelay: `${index * 0.5}s` }} />
            </div>

            {/* Expandable content */}
            {expanded && (
              <div className="agent-sub-window__content">
                <List>
                  {React.Children.map(item.details.props.children, wrapWithSpinner)}
                </List>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ExpandableList;
